import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Divider,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Select,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { NumericFormat } from "react-number-format";
import { useRecoilState } from "recoil";
import jwt_decode from "jwt-decode";
import { customColors } from "../../../atoms/customColors";
import { JWTToken } from "../../../routes/privateRoute";
import SimulatorAPI, {
  OpcoesSimulacao,
  ProdutoOpcao,
  Simulacao,
} from "../../../services/simulatorAPI";

// Simulador de vendas — v2.
//
// O que a v1 fazia e não funcionava:
//   - pedia BANDEIRA e FORMA separadas, então o lojista tinha que escolher
//     "Visa" para simular um boleto;
//   - chamava os modos de "DIRETA" e "REVERSA", que não dizem nada para quem
//     vende — e foi exatamente essa confusão que gerou reclamação de cliente
//     ("os juros de uma venda de 100 mil estão errados", quando a tela estava
//     respondendo "quanto vender para receber 100 mil");
//   - ao fechar, o modal resetava o modo para REVERSA (o estado inicial era
//     DIRETA), então a segunda simulação vinha em outro modo sem aviso;
//   - mandava a bandeira 15 (Geral) para BolePix, e o nome procurado na lista
//     ("BolePix") nunca batia com o cadastrado ("Bolepix"): a simulação de
//     BolePix simplesmente não funcionava;
//   - mostrava só o valor final e "Taxa de X%", sem abrir a conta.
//
// A v2 pergunta uma coisa por vez, abre a conta inteira e diz de onde veio a
// taxa. O cálculo é todo da core (endpoint /Simulador) — aqui não se faz conta,
// só se exibe.
//
// O botão que abre este modal (formato de maquininha, components/simulator.tsx)
// não faz parte da v2 e segue como está.

type IdMetodo = "CREDITO" | "DEBITO" | "PIX" | "BOLETO" | "BOLEPIX";

interface Metodo {
  id: IdMetodo;
  rotulo: string;
  // bandeira fixa para formas que não são cartão
  idBandeira?: number;
  // forma fixa; no crédito ela depende do número de parcelas
  forma?: string;
  precisaBandeira?: boolean;
  parcelado?: boolean;
}

// sysBandeira: 13 Boleto · 14 PIX · 16 Bolepix.
// BolePix Pix QRCode (forma BPP) existe no cadastro mas ficou de fora por
// decisão de produto — praticamente não é operado.
// Sem descrição por forma de recebimento de propósito: o prazo depende do plano
// de cada cliente (há cliente com PIX em D+30), e o cadastro do plano não guarda
// esse texto. Prometer "na hora" ou "D+1" aqui seria mentira para parte da base.
const METODOS: Metodo[] = [
  { id: "CREDITO", rotulo: "Crédito", precisaBandeira: true, parcelado: true },
  { id: "DEBITO", rotulo: "Débito", precisaBandeira: true, forma: "D" },
  { id: "PIX", rotulo: "PIX", idBandeira: 14, forma: "P" },
  { id: "BOLETO", rotulo: "Boleto", idBandeira: 13, forma: "B" },
  { id: "BOLEPIX", rotulo: "Gestão de Boletos", idBandeira: 16, forma: "BP" },
];

// os planos vão até 21x; o cadastro ainda tem linhas de C22 a C24 de planos
// antigos, que não entram na lista
const MAX_PARCELAS = 21;

const PRODUTO_PADRAO = 1;

const dinheiro = (valor: number) =>
  valor.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const percentual = (valor: number) => `${valor.toFixed(2).replace(".", ",")}%`;

export default function SalesSimulator({ isOpen, setIsOpen }: any) {
  const cores = useRecoilState(customColors)[0];

  const token = window.localStorage.getItem("token");
  const jwtToken: JWTToken = jwt_decode(token || "");
  const idCliente = Number(jwtToken.idCliente);

  const [opcoes, setOpcoes] = useState<OpcoesSimulacao | null>(null);

  const [metodo, setMetodo] = useState<IdMetodo | null>(null);
  const [idBandeira, setIdBandeira] = useState(0);
  const [parcelas, setParcelas] = useState(1);
  const [idProduto, setIdProduto] = useState(PRODUTO_PADRAO);
  // false = "quero vender este valor" · true = "quero receber este valor"
  const [reversa, setReversa] = useState(false);
  const [valor, setValor] = useState<number | null>(null);

  const [resultado, setResultado] = useState<Simulacao | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const metodoAtual = useMemo(
    () => METODOS.find((m) => m.id === metodo) ?? null,
    [metodo]
  );

  // suporte de um produto à forma escolhida — é o cruzamento que define tanto os
  // métodos que aparecem quanto os tipos de venda oferecidos para cada um
  const suporta = useCallback((produto: ProdutoOpcao, id: IdMetodo) => {
    if (id === "CREDITO") return produto.bandeirasCredito.length > 0;
    if (id === "DEBITO") return produto.bandeirasDebito.length > 0;
    if (id === "PIX") return produto.temPix;
    if (id === "BOLETO") return produto.temBoleto;
    return produto.temBolepix;
  }, []);

  // método aparece se ALGUM tipo de venda o suporta
  const metodosDisponiveis = useMemo(() => {
    if (!opcoes) return METODOS;
    return METODOS.filter((m) => opcoes.produtos.some((p) => suporta(p, m.id)));
  }, [opcoes, suporta]);

  // e o tipo de venda só lista o que casa com a forma escolhida: boleto não
  // existe em Tap2Pay, e venda digitada só roda crédito
  const produtosDisponiveis = useMemo(() => {
    if (!opcoes) return [];
    if (!metodo) return opcoes.produtos;
    return opcoes.produtos.filter((p) => suporta(p, metodo));
  }, [opcoes, metodo, suporta]);

  const produtoAtual = useMemo(
    () => produtosDisponiveis.find((p) => p.id === idProduto) ?? null,
    [produtosDisponiveis, idProduto]
  );

  // débito não existe em Visa nem Mastercard: são bandeiras diferentes das do
  // crédito, e a lista vem pronta da core
  const bandeirasDoMetodo = useMemo(() => {
    if (!produtoAtual || !metodoAtual?.precisaBandeira) return [];
    return metodoAtual.id === "DEBITO"
      ? produtoAtual.bandeirasDebito
      : produtoAtual.bandeirasCredito;
  }, [produtoAtual, metodoAtual]);

  const maxParcelas = Math.min(produtoAtual?.maxParcelas || MAX_PARCELAS, MAX_PARCELAS);

  useEffect(() => {
    if (!isOpen) return;

    (async () => {
      try {
        setOpcoes(await SimulatorAPI.opcoes(idCliente));
      } catch {
        setOpcoes(null);
        setErro("Não foi possível carregar as formas de recebimento do seu plano.");
      }
    })();
  }, [isOpen, idCliente]);

  const idFormaAtual = useMemo(() => {
    if (!metodoAtual) return "";
    if (metodoAtual.parcelado) return `C${String(parcelas).padStart(2, "0")}`;
    return metodoAtual.forma ?? "";
  }, [metodoAtual, parcelas]);

  const idBandeiraAtual = metodoAtual?.idBandeira ?? idBandeira;

  const podeCalcular =
    !!metodoAtual && idBandeiraAtual > 0 && !!idFormaAtual && (valor ?? 0) > 0;

  const simular = useCallback(async () => {
    if (!podeCalcular) return;

    setCarregando(true);
    setErro(null);

    try {
      const dados = await SimulatorAPI.simular({
        idCliente,
        idProduto,
        idBandeira: idBandeiraAtual,
        idForma: idFormaAtual,
        valor: valor ?? 0,
        reversa,
      });
      setResultado(dados);
    } catch (e: any) {
      setResultado(null);
      // a core devolve o motivo em texto; genérico só quando não vier nada
      const detalhe = e?.response?.data;
      setErro(
        typeof detalhe === "string" && detalhe.trim()
          ? detalhe
          : "Não foi possível simular agora. Tente novamente."
      );
    } finally {
      setCarregando(false);
    }
  }, [
    podeCalcular,
    idCliente,
    idProduto,
    idBandeiraAtual,
    idFormaAtual,
    valor,
    reversa,
  ]);

  // recalcula sozinho quando os dados mudam — sem botão para esquecer de clicar
  useEffect(() => {
    if (!podeCalcular) {
      setResultado(null);
      return;
    }

    const timer = setTimeout(simular, 450);
    return () => clearTimeout(timer);
  }, [simular, podeCalcular]);

  // o tipo de venda escolhido pode não existir na forma nova (boleto em Tap2Pay,
  // por exemplo): cai no primeiro compatível em vez de ficar numa seleção que
  // não calcula
  useEffect(() => {
    if (!metodo || produtosDisponiveis.length === 0) return;
    if (produtosDisponiveis.some((p) => p.id === idProduto)) return;

    setIdProduto(produtosDisponiveis[0].id);
    setIdBandeira(0);
  }, [produtosDisponiveis, idProduto, metodo]);

  function escolherMetodo(novo: Metodo) {
    setMetodo(novo.id);
    setResultado(null);
    setErro(null);
    // crédito e débito têm bandeiras diferentes: manter a anterior selecionaria
    // uma que não existe na nova modalidade
    setIdBandeira(0);
    if (!novo.parcelado) setParcelas(1);
  }

  function fechar() {
    setIsOpen(false);
    // volta ao estado inicial de verdade: a v1 reabria em outro modo
    setMetodo(null);
    setIdBandeira(0);
    setParcelas(1);
    setIdProduto(PRODUTO_PADRAO);
    setReversa(false);
    setValor(null);
    setResultado(null);
    setErro(null);
  }

  return (
    <Modal isOpen={isOpen} onClose={fechar} size="4xl" isCentered scrollBehavior="inside">
      <ModalOverlay bgColor="rgba(0,0,0,0.45)" />
      <ModalContent borderRadius="20px" overflow="hidden" mx={3}>
        <ModalCloseButton color={cores.WHITE} zIndex={2} />

        <ModalBody p={0}>
          <Flex direction={{ base: "column", md: "row" }} minH="520px">
            {/* ─── escolhas ─────────────────────────────────────────── */}
            <Flex
              flex="1"
              direction="column"
              gap={5}
              p={{ base: 5, md: 7 }}
              bgColor={cores.WHITE}
            >
              <Box>
                <Text fontSize="1.4rem" fontWeight="bold" color={cores.BLUEMONEDD}>
                  Simule uma venda
                </Text>
                <Text fontSize="sm" color={cores.GRAY}>
                  Taxas e prazos de recebimento conforme o seu plano Monedd.
                </Text>
                {opcoes?.plano && (
                  <Text fontSize="xs" color={cores.GRAY} mt={1}>
                    Plano: {opcoes.plano}
                  </Text>
                )}
              </Box>

              <Box>
                <Rotulo>Como você vai receber?</Rotulo>
                <Flex gap={2} wrap="wrap">
                  {metodosDisponiveis.map((item) => {
                    const ativo = metodo === item.id;
                    return (
                      <Button
                        key={item.id}
                        onClick={() => escolherMetodo(item)}
                        size="sm"
                        h="auto"
                        py={2}
                        px={3}
                        borderRadius="12px"
                        border="1px solid"
                        borderColor={ativo ? cores.BLUEMONEDD : cores.GRAY_LIGHTER}
                        bgColor={ativo ? cores.BLUEMONEDD : cores.WHITE}
                        color={ativo ? cores.WHITE : cores.DARK}
                        _hover={{ borderColor: cores.BLUEMONEDD }}
                      >
                        <Text fontWeight="600" fontSize="sm">
                          {item.rotulo}
                        </Text>
                      </Button>
                    );
                  })}
                </Flex>
              </Box>

              {metodoAtual?.precisaBandeira && (
                <Box>
                  <Rotulo>Bandeira</Rotulo>
                  <Select
                    size="sm"
                    borderRadius="10px"
                    value={idBandeira}
                    onChange={(e) => setIdBandeira(Number(e.target.value))}
                  >
                    <option value={0}>Selecione a bandeira</option>
                    {bandeirasDoMetodo.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.nome}
                      </option>
                    ))}
                  </Select>
                </Box>
              )}

              {metodoAtual?.parcelado && (
                <Box>
                  <Rotulo>Em quantas vezes?</Rotulo>
                  <Select
                    size="sm"
                    borderRadius="10px"
                    value={parcelas}
                    onChange={(e) => setParcelas(Number(e.target.value))}
                  >
                    {Array.from({ length: maxParcelas }, (_, i) => i + 1).map((n) => (
                      <option key={n} value={n}>
                        {n === 1 ? "À vista" : `${n}x`}
                      </option>
                    ))}
                  </Select>
                </Box>
              )}

              {metodoAtual && produtosDisponiveis.length > 1 && (
                <Box>
                  <Rotulo>Tipo de venda</Rotulo>
                  <Select
                    size="sm"
                    borderRadius="10px"
                    value={idProduto}
                    onChange={(e) => setIdProduto(Number(e.target.value))}
                  >
                    {produtosDisponiveis.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.nome}
                      </option>
                    ))}
                  </Select>
                </Box>
              )}

              {metodoAtual && (
                <Box>
                  <Rotulo>O que você quer calcular?</Rotulo>
                  <Flex
                    borderRadius="12px"
                    border="1px solid"
                    borderColor={cores.GRAY_LIGHTER}
                    overflow="hidden"
                    mb={3}
                  >
                    <Opcao
                      ativa={!reversa}
                      cores={cores}
                      onClick={() => setReversa(false)}
                    >
                      Quero vender
                    </Opcao>
                    <Opcao
                      ativa={reversa}
                      cores={cores}
                      onClick={() => setReversa(true)}
                    >
                      Quero receber
                    </Opcao>
                  </Flex>

                  <Rotulo>
                    {reversa
                      ? "Valor que você quer receber"
                      : "Valor da venda"}
                  </Rotulo>
                  <NumericFormat
                    customInput={Input2}
                    value={valor ?? ""}
                    thousandSeparator="."
                    decimalSeparator=","
                    decimalScale={2}
                    fixedDecimalScale
                    allowNegative={false}
                    prefix="R$ "
                    placeholder="R$ 0,00"
                    inputMode="decimal"
                    onValueChange={(v) => setValor(v.floatValue ?? null)}
                  />
                </Box>
              )}
            </Flex>

            {/* ─── resultado ────────────────────────────────────────── */}
            <Flex
              flex="1"
              direction="column"
              justify="center"
              gap={4}
              p={{ base: 5, md: 7 }}
              bgColor={cores.BLUEMONEDDBUTTONS}
              color={cores.WHITE}
            >
              {carregando ? (
                <Flex justify="center" align="center" h="100%">
                  <Spinner />
                </Flex>
              ) : erro ? (
                <Flex direction="column" gap={3} align="flex-start">
                  <Text fontWeight="bold">Não deu para simular</Text>
                  <Text fontSize="sm" opacity={0.9}>
                    {erro}
                  </Text>
                  <Button size="sm" onClick={simular} variant="outline" color={cores.WHITE}>
                    Tentar de novo
                  </Button>
                </Flex>
              ) : !resultado ? (
                <Flex direction="column" gap={2} opacity={0.85}>
                  <Text fontWeight="bold" fontSize="1.1rem">
                    Sua simulação aparece aqui
                  </Text>
                  <Text fontSize="sm">
                    Escolha como vai receber e informe o valor. O cálculo é
                    automático.
                  </Text>
                </Flex>
              ) : (
                <>
                  <Box>
                    <Text fontSize="xs" letterSpacing="0.1em" opacity={0.8}>
                      {reversa ? "PARA RECEBER" : "SE VOCÊ VENDER"}{" "}
                      {dinheiro(reversa ? resultado.valorLiquido : resultado.valorVenda)}
                    </Text>
                    <Text fontSize="sm" opacity={0.9}>
                      {reversa ? "você precisa vender" : "você recebe"}
                    </Text>
                    <Text fontSize="2.6rem" fontWeight="bold" lineHeight="1.1">
                      {dinheiro(
                        reversa ? resultado.valorVenda : resultado.valorLiquido
                      )}
                    </Text>
                  </Box>

                  <Divider opacity={0.25} />

                  <Flex direction="column" gap={1.5} fontSize="sm">
                    <LinhaConta rotulo="Valor da venda">
                      {dinheiro(resultado.valorVenda)}
                    </LinhaConta>

                    <LinhaConta rotulo={`Taxa (${percentual(resultado.percentual)})`}>
                      − {dinheiro(resultado.valorPercentual)}
                    </LinhaConta>

                    {resultado.tarifaFixa > 0 && (
                      <LinhaConta rotulo="Tarifa por transação">
                        − {dinheiro(resultado.tarifaFixa)}
                      </LinhaConta>
                    )}

                    <Divider opacity={0.25} my={1} />

                    <LinhaConta rotulo="Custo total" forte>
                      {dinheiro(resultado.custoTotal)}
                    </LinhaConta>

                    <LinhaConta rotulo="Você recebe" forte>
                      {dinheiro(resultado.valorLiquido)}
                    </LinhaConta>
                  </Flex>

                  {reversa && (
                    <Text fontSize="xs" opacity={0.85}>
                      A taxa incide sobre o valor da venda. Por isso o custo de{" "}
                      {dinheiro(resultado.custoTotal)} equivale a{" "}
                      {percentual(resultado.custoSobreLiquido)} do que você recebe.
                    </Text>
                  )}

                  {resultado.alerta && (
                    <Box bgColor="rgba(0,0,0,0.2)" p={2} borderRadius="8px">
                      <Text fontSize="xs">{resultado.alerta}</Text>
                    </Box>
                  )}

                  <Box borderTop="1px solid" borderColor="rgba(255,255,255,0.25)" pt={3}>
                    {resultado.planoCliente && (
                      <Text fontSize="xs" opacity={0.85}>
                        Plano: {resultado.planoCliente}
                      </Text>
                    )}
                    {resultado.tarifaCliente && (
                      <Text fontSize="xs" opacity={0.85}>
                        Tarifa: {resultado.tarifaCliente}
                      </Text>
                    )}
                    {resultado.origemTaxa === "PADRAO" && (
                      <Text fontSize="xs" mt={1} fontWeight="600">
                        {resultado.origemDescricao}
                      </Text>
                    )}
                  </Box>
                </>
              )}
            </Flex>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

function Rotulo({ children }: any) {
  return (
    <Text fontSize="xs" fontWeight="600" mb={1.5} color="#4A5568" letterSpacing="0.04em">
      {children}
    </Text>
  );
}

function Opcao({ ativa, cores, onClick, children }: any) {
  return (
    <Button
      flex="1"
      size="sm"
      borderRadius="0"
      bgColor={ativa ? cores.BLUEMONEDD : cores.WHITE}
      color={ativa ? cores.WHITE : cores.GRAY}
      _hover={{ bgColor: ativa ? cores.BLUEMONEDD : cores.GRAY_LIGHTEST }}
      onClick={onClick}
    >
      {children}
    </Button>
  );
}

function LinhaConta({ rotulo, forte, children }: any) {
  return (
    <Flex justify="space-between" gap={4} fontWeight={forte ? "700" : "400"}>
      <Text>{rotulo}</Text>
      <Text>{children}</Text>
    </Flex>
  );
}

// input do NumericFormat: mesmo visual dos selects ao lado
function Input2(props: any) {
  return (
    <input
      {...props}
      style={{
        width: "100%",
        height: "44px",
        borderRadius: "10px",
        border: "1px solid #E2E8F0",
        padding: "0 12px",
        fontSize: "1.15rem",
        fontWeight: 600,
        color: "#1A202C",
        outline: "none",
      }}
    />
  );
}
